import useSWRImmutable from 'swr/immutable';
import isArray from 'lodash/isArray';
import { getContentfulSettings, getEntries, isErrorResponse } from '@belong/contentful';
import { logger } from '@belong/logging';
import { IDictionary, IOfferCampaign, PRODUCT_FAMILY } from '@belong/types';
import { applyDictionaryRecursively } from '@belong/type-utils';
import { CampaignContentField } from '../OfferContentManager';
import { FOfferCampaign } from '../factories';
import { offerToDictionary } from '../templates';

export interface IUseCampaignOverride<F extends CampaignContentField> {
  isLoading: boolean;
  error?: Error;
  override?: IOfferCampaign[F];
}

// Extract the requested field from the campaign data
const extractField = <F extends CampaignContentField>(
  fieldName: F,
  productFamily?: PRODUCT_FAMILY | PRODUCT_FAMILY[],
  campaign?: IOfferCampaign
): IOfferCampaign[F] | undefined => {
  // No cache-key or campaign data - return undefined
  const eligibleProductFamilies = isArray(productFamily) ? productFamily : [productFamily];
  if (!campaign || !eligibleProductFamilies.length) {
    return;
  }

  // No offers for the product family - return undefined
  if (!campaign.offers.some(offer => eligibleProductFamilies.includes(offer.productFamily))) {
    return;
  }

  // Only expiry dates are valid in Campaign content
  const endDateFields = Object.entries(offerToDictionary(campaign?.offers[0])).reduce((acc, [key, value]) => {
    if (key.startsWith('offer:expiry')) {
      acc[key] = value;
    }
    return acc;
  }, {} as IDictionary);
  return applyDictionaryRecursively(endDateFields, campaign[fieldName]);
};

const loadOverride = async (fieldName: CampaignContentField): Promise<IOfferCampaign | undefined> => {
  const result = await getEntries(
    {
      content_type: 'offerCampaign',
      'fields.isActive': true,
      select: [`fields.${fieldName}`, 'fields.offers'].join(','),
      limit: 1
    },
    getContentfulSettings()
  );

  if (isErrorResponse(result)) {
    throw new Error(`Error fetching campaign override for field: ${fieldName}`);
  }

  // No active campaigns - return undefined (not an error scenario)
  if (!result.length) {
    return;
  }

  return FOfferCampaign(result[0]);
};

// Load specific field from the active Offer Campaign
// Notes:
//  - Using SWRImmutable to prevent re-fetching/revalidation on tab change or focus
//  - Does NOT fetch data unless fieldName and productFamily are present
//  - Returns undefined if no active campaign or no offers for the product family
//  - Uses the fieldName to resolve the correct data type for the override
export const useCampaignOverride = <F extends CampaignContentField>(
  fieldName: F,
  productFamily?: PRODUCT_FAMILY | PRODUCT_FAMILY[],
  shouldFetch = true
): IUseCampaignOverride<F> => {
  const hasRequiredParams = fieldName && (isArray(productFamily) ? productFamily.length : productFamily);
  const key = shouldFetch && hasRequiredParams ? `campaign-${fieldName}` : false;
  const { isLoading, error, data } = useSWRImmutable(key, () => loadOverride(fieldName));
  const override = extractField(fieldName, productFamily, data);

  if (error) {
    logger.error('[useCampaignOverride]', error);
  }

  return {
    isLoading,
    error,
    override
  };
};
